import * as React from 'react';
import {useContext} from 'react';
import {
  view,
  animate,
  createViewConfig,
  media,
  useTokenValue,
  toIntent,
  assignProps,
} from '@sail/engine';
import type {View} from '../../view';
import {tokens} from '../../tokens';
import {css, CssProperties} from '../../css';
import {Layer} from '../layers/Layer';
import {Backdrop} from '../../components/Backdrop';
import {BottomSheetOverlay} from '../BottomSheetOverlay';
import {FocusScope} from '../layers/FocusScope';
import type {MobileDisplay} from '../../utilStyles/sheet';
import {useUiConfig} from '../../providers/UiConfigProvider';
import {useOverlayIframe} from '../../iframe-overrides/OverlayOverrides';
import {useRpcMediaMaxWidth} from '../hooks/useMatchMedia/useRpcMatchMedia';

const drawerIframeOverrides = toIntent([assignProps(useOverlayIframe)]);

export const DrawerContext = React.createContext<
  {onClose: () => void} | undefined
>(undefined);

export function useDrawerClose(): (() => void) | undefined {
  const context = useContext(DrawerContext);
  return context ? context.onClose : undefined;
}

type DrawerSize = 'small' | 'medium' | 'large';

export type BaseDrawerProps = View.IntrinsicElement<'div'> & {
  blocking?: boolean;
  size?: DrawerSize;
  inline?: boolean;
  layerName?: string;
  onClose: () => void;
  header?: React.ReactNode | (() => void);
  start?: React.ReactNode | (() => void);
  children?: React.ReactNode;
  end?: React.ReactNode | (() => void);
  subviews?: View.Subviews<{
    layer: typeof Layer;
    content: 'div';
    header: 'div';
  }>;
  preferredMobileDisplay?: MobileDisplay;
};

export const BaseDrawerConfig = createViewConfig({
  name: 'BaseDrawer',
  props: {} as BaseDrawerProps,
  defaults: {
    size: 'small',
    inline: false,
  },
});

const minWidths: Record<DrawerSize, number> = {
  small: 344,
  medium: 480,
  large: 584,
};

function getSizeBasedMediaQueries(
  shouldDisplayMobileUI: boolean,
  size: DrawerSize = 'small',
  extra: Partial<
    Record<DrawerSize, Partial<Record<'mobile' | 'desktop', CssProperties>>>
  > = {},
) {
  const sizeExtraCss = extra[size] ?? {};
  const baseStyles = shouldDisplayMobileUI
    ? {width: 'fill', ...(sizeExtraCss.mobile ?? {})}
    : {
        width: `clamp(min(${minWidths.small}px, 100vw), 20vw, 100vw)`,
        ...(sizeExtraCss.desktop ?? {}),
      };

  const mediumStyles = size === 'medium' && {
    ...(shouldDisplayMobileUI
      ? {width: 'fill', ...(sizeExtraCss.mobile ?? {})}
      : {
          width: `clamp(min(${minWidths.medium}px, 100vw), 35vw, 100vw)`,
          ...(sizeExtraCss.desktop ?? {}),
        }),
  };

  const largeStyles = size === 'large' && {
    ...(shouldDisplayMobileUI
      ? {width: 'fill', ...(sizeExtraCss.mobile ?? {})}
      : {
          width: `clamp(min(${minWidths.large}px, 100vw), 45vw, 100vw)`,
          ...(sizeExtraCss.desktop ?? {}),
        }),
  };

  return {
    ...baseStyles,
    ...mediumStyles,
    ...largeStyles,
  } as CssProperties;
}

// If the `end` content (preview) renders below this size, instead of side scrolling within its own
// container, the entire drawer will side scroll.
const MINIMUM_PREVIEW_SIZE = 250;

export const useShouldScroll = (size: DrawerSize = 'small') => {
  return useRpcMediaMaxWidth(`${minWidths[size] + MINIMUM_PREVIEW_SIZE}px`);
};

export const BaseDrawer = BaseDrawerConfig.createView(
  ({
    blocking = true,
    size,
    layerName,
    onClose,
    header,
    start,
    children,
    end,
    subviews,
    ...props
  }) => {
    const uiConfig = useUiConfig();
    const isSmallScreen = !!uiConfig?.isSmallScreen;
    const shouldApplyIframeOverrides = !!uiConfig?._unstable_requestIframe;
    const easing = useTokenValue(tokens.animation.easing) as string;

    const durationShort = parseInt(
      useTokenValue(tokens.animation.duration.short) as string,
      10,
    );

    const durationLong = parseInt(
      useTokenValue(tokens.animation.duration.short) as string,
      10,
    );
    const shouldScroll = useShouldScroll(size);

    return (
      <Layer
        inherits={subviews.layer}
        name={layerName || (blocking ? 'modal' : 'nonmodal')}
        uses={shouldApplyIframeOverrides ? drawerIframeOverrides : undefined}
      >
        {blocking ? (
          <Backdrop
            onPress={onClose}
            uses={[
              animate.in({
                duration: durationShort,
                opacity: 0,
                easing,
              }),
              animate.out({
                duration: durationLong,
                opacity: 0,
                easing,
              }),
            ]}
          />
        ) : null}
        <FocusScope contain={blocking} restoreFocus>
          <view.div tabIndex={-1} role="dialog" {...props}>
            <BottomSheetOverlay />
            <view.div
              inherits={subviews.header}
              css={{
                height: 'fit',
                ...getSizeBasedMediaQueries(isSmallScreen, size),
              }}
            >
              {header && header}
            </view.div>
            <view.div
              css={{
                stack: 'x',
                alignY: 'stretch',
                minHeight: 'space.0',
                height: 'fill',
                ...(shouldScroll ? {overflowX: 'scroll'} : {}),
              }}
            >
              {start}
              <view.div
                css={{
                  ...getSizeBasedMediaQueries(isSmallScreen, size, {
                    small: {
                      mobile: {
                        font: 'body.medium',
                      },
                      desktop: {
                        font: 'body.small',
                      },
                    },
                  }),
                  stack: 'y',
                  height: 'fill',
                  position: 'relative',
                  overflow: 'auto',
                }}
                inherits={subviews.content}
                uses={[shouldScroll ? css({flexShrink: '0'}) : null]}
              >
                {children}
              </view.div>
              {end}
            </view.div>
          </view.div>
        </FocusScope>
      </Layer>
    );
  },
  {
    // Fixed position is important here, not just to make sure the drawer always
    // appears correctly regardless of scrolling, but also for animation
    // performance. Fixed is guaranteed to not effect scrolling of the window, so
    // when the content goes offscreen the animation is smoother
    css: {
      borderColor: 'border',
      borderWidth: 'small',
      borderStyle: 'solid',
      borderRightWidth: 0,
      backgroundColor: 'surface',
      color: 'text',
      position: 'fixed',
      top: 'calc(var(--sail-layer-margin-top, 0px) - 1px)',
      right: 'space.0',
      bottom: 'calc(var(--sail-layer-margin-bottom, 0px) - 1px)',
      transformOrigin: 'center left',
      overflow: 'hidden',
      stack: 'y',
      focusRing: 'none',
      maxWidth: '100vw',
      outline: 'none',
    },
    variants: {
      inline: {
        false: css({
          borderRadius: `${tokens.border.radius.small} 0 0 ${tokens.border.radius.small}`,
          [media.up(tokens.viewport.mobile)]: {
            boxShadow: 'overlay',
          },
        }),
      },
    },
    forward: {size: true},
  },
);
